import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useApi } from '../context/ApiProvider';
import PageHeader from '../components/PageHeader';
import LoadingScreen from '../components/LoadingScreen';
import AdminActivityTable from '../components/AdminActivityTable';
import defaultArt from '../assets/images/dj_kip_art.png';

const PERIODS = [
  { label: 'All time', days: null },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

const n = (key, label) => ({ key, label, type: 'number' });
const d = (key, label) => ({ key, label, type: 'date' });
const s = (key, label) => ({ key, label, type: 'string' });

const CONTENT_URL = {
  set: (id) => `/sets/${id}`,
  track: (id) => `/tracks/${id}`,
  artist: (id) => `/artists/${id}`,
};

// What each admin view lists, its columns, and where a row leads.
const VIEWS = {
  users: {
    title: 'Users',
    blurb:
      'Logins and activity per user: when they joined and were last seen, who referred them, and the plays, likes, buys, follows and share links they made',
    sort: 'last_seen',
    columns: [
      s('username', 'User'),
      s('email', 'Email'),
      s('referred_by', 'Referred by'),
      d('first_seen', 'Joined'),
      d('last_seen', 'Last seen'),
      n('plays', 'Plays'),
      n('sets_played', 'Sets played'),
      n('likes', 'Likes'),
      n('set_favs', 'Set favs'),
      n('track_favs', 'Track favs'),
      n('buys', 'Buys'),
      n('follows', 'Follows'),
      n('shares', 'Shares'),
      n('share_clicks', 'Share clicks'),
      n('share_signups', 'Share signups'),
    ],
  },
  sets: {
    title: 'Sets',
    blurb:
      'Sets people play and like: plays and listeners, likes, favourites, shares, and how many of each set’s tracks are still IDs',
    sort: 'plays',
    rowUrl: (row) => `/sets/${row.id}`,
    columns: [
      s('set_name', 'Set'),
      s('artist', 'Artist'),
      d('release_date', 'Released'),
      n('plays', 'Plays'),
      n('listeners', 'Listeners'),
      n('likes', 'Likes'),
      n('likers', 'Likers'),
      n('favs', 'Favs'),
      n('shares', 'Shares'),
      n('share_clicks', 'Share clicks'),
      {
        key: 'track_id_count',
        label: 'IDs',
        type: 'number',
        render: (row) =>
          row.track_count
            ? `${row.track_id_count ?? 0} / ${row.track_count}`
            : '-',
      },
      d('last_played', 'Last played'),
    ],
  },
  tracks: {
    title: 'Tracks',
    blurb:
      'Tracks people like, favourite, buy and share. Plays belong to sets, so tracks have none of their own',
    sort: 'likes',
    rowUrl: (row) => `/tracks/${row.id}`,
    columns: [
      s('title', 'Track'),
      s('artist', 'Artist'),
      s('mix_artist', 'Remixer'),
      s('label_name', 'Label'),
      n('likes', 'Likes'),
      n('likers', 'Likers'),
      n('favs', 'Favs'),
      n('buys', 'Buys'),
      n('shares', 'Shares'),
      n('share_clicks', 'Share clicks'),
      n('found_in_sets', 'In sets'),
    ],
  },
  artists: {
    title: 'Artists',
    blurb:
      'Artists people follow and listen to: followers, fans, and the plays, likes and buys kip counts for them',
    sort: 'followers',
    rowUrl: (row) => `/artists/${row.id}`,
    columns: [
      s('name', 'Artist'),
      n('followers', 'Followers'),
      n('fans', 'Fans'),
      n('plays', 'Plays'),
      n('likes', 'Likes'),
      n('buys', 'Buys'),
      n('shares', 'Shares'),
      n('share_clicks', 'Share clicks'),
      n('set_count', 'Sets'),
      n('track_count', 'Tracks'),
    ],
  },
  shares: {
    title: 'Share links',
    blurb:
      'Every share link: who made it, what it points to, its clicks, and the people who signed up through it',
    sort: 'created_at',
    rowUrl: (row) => CONTENT_URL[row.content_type]?.(row.content_id),
    columns: [
      d('created_at', 'Created'),
      s('sharer', 'Shared by'),
      s('content_type', 'Type'),
      s('content_title', 'Shared'),
      n('clicks', 'Clicks'),
      d('last_click', 'Last click'),
      n('signups', 'Signups'),
    ],
  },
};

const SUMMARY = [
  ['active_users', 'Active users'],
  ['new_users', 'New users'],
  ['plays', 'Plays'],
  ['listeners', 'Listeners'],
  ['likes', 'Likes'],
  ['set_favs', 'Set favs'],
  ['track_favs', 'Track favs'],
  ['buys', 'Buys'],
  ['follows', 'Follows'],
  ['shares', 'Shares'],
  ['share_clicks', 'Share clicks'],
  ['share_signups', 'Share signups'],
];

function sinceDate(days) {
  if (!days) return undefined;
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function AdminActivity({ kind }) {
  const view = VIEWS[kind];
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(null);
  const [sort, setSort] = useState(view.sort);
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [includeDemo, setIncludeDemo] = useState(false);

  const handleError = useCallback(
    (response, fallback) => {
      enqueueSnackbar(
        response.status === 403
          ? 'Admin access required'
          : response.body?.description || response.body?.message || fallback,
        { variant: 'error' }
      );
    },
    [enqueueSnackbar]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const filters = {
      since: sinceDate(days),
      include_demo: includeDemo || undefined,
    };
    Object.keys(filters).forEach(
      (key) => filters[key] === undefined && delete filters[key]
    );
    const [list, totals] = await Promise.all([
      api.get(`/admin/activity/${kind}`, {
        ...filters,
        sort,
        order,
        limit: perPage,
        offset: page * perPage,
        ...(query ? { q: query } : {}),
      }),
      api.get('/admin/activity/summary', filters),
    ]);
    if (list.ok) {
      setRows(list.body.rows || []);
      setTotal(list.body.total || 0);
      setNotes(list.body.notes || []);
    } else {
      handleError(list, `Failed to load ${view.title.toLowerCase()}`);
      setRows([]);
      setTotal(0);
    }
    if (totals.ok) setSummary(totals.body);
    setLoading(false);
  }, [
    api,
    kind,
    days,
    includeDemo,
    sort,
    order,
    page,
    perPage,
    query,
    handleError,
    view.title,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (key, type) => {
    if (key === sort) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(key);
      setOrder(type === 'string' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const handleRowClick = view.rowUrl
    ? (row) => {
        const url = view.rowUrl(row);
        if (url) navigate(url);
      }
    : undefined;

  return (
    <>
      {loading && <LoadingScreen />}
      <PageHeader backgroundArt={defaultArt} minHeight={350}>
        <Typography variant="h3" fontWeight="bold" mt={4}>
          Admin{' '}
          <Typography
            variant="h3"
            component="span"
            color="primary"
            fontWeight="bold"
          >
            {view.title}
          </Typography>
        </Typography>
        <Typography mt={2}>{view.blurb}</Typography>
      </PageHeader>
      <Box sx={{ padding: { xs: '20px', lg: '30px 0' } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mb: 2,
          }}
        >
          {PERIODS.map(({ label, days: value }) => (
            <Chip
              key={label}
              label={label}
              color={days === value ? 'primary' : 'default'}
              onClick={() => {
                setDays(value);
                setPage(0);
              }}
              clickable
            />
          ))}
          <FormControlLabel
            sx={{ ml: 1 }}
            control={
              <Switch
                size="small"
                checked={includeDemo}
                onChange={(event) => {
                  setIncludeDemo(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Include demo accounts"
          />
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setQuery(search.trim());
                setPage(0);
              }
            }}
            onBlur={() => {
              if (search.trim() !== query) {
                setQuery(search.trim());
                setPage(0);
              }
            }}
          />
        </Box>

        {summary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {SUMMARY.map(([key, label]) => (
              <Chip
                key={key}
                variant="outlined"
                label={`${label}: ${(summary[key] ?? 0).toLocaleString()}`}
              />
            ))}
          </Box>
        )}

        {!loading && rows.length === 0 && (
          <Typography color="text.secondary">
            No activity in this period.
          </Typography>
        )}

        <AdminActivityTable
          columns={view.columns}
          rows={rows}
          sort={sort}
          order={order}
          onSort={handleSort}
          onRowClick={handleRowClick}
          label={`admin ${kind} activity`}
        />
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, value) => setPage(value)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        )}
        {notes.map((note) => (
          <Typography key={note} variant="body2" color="text.secondary" mt={1}>
            {note}
          </Typography>
        ))}
      </Box>
    </>
  );
}

AdminActivity.propTypes = {
  kind: PropTypes.oneOf(Object.keys(VIEWS)).isRequired,
};

export default AdminActivity;
